import { Button as UiButton, Icon as UiIcon } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import EmailLink from '~/components2/EmailLink';
import Icon from '~/components2/Icon';
import Popover from '~/components2/Popover/Popover';
import AgencyDataStatus from '../AgencyDataStatus';
import store from '../store';
import { getColorFromRgb, type IntelligenceStatus, NO_CONFLICTS, PENDING } from '.';
import Comment from './Comment';
import FeatureDetailCard from './FeatureDetailCard';
import StatusSelect from './StatusSelect';
import { useShouldOpen } from './useShouldOpen';

const ContactSupport = () => {
  return (
    <>
      Project data for this section has not yet been digitized. Please contact{' '}
      <EmailLink>support@civilgrid.com</EmailLink> for assistance obtaining this data.
    </>
  );
};

export const AgencyStatusMap: { [key: number]: React.ReactNode } = {
  1: <ContactSupport />,
  2: 'This agency reported that they do not have any active paving moratoria, but it is recommended to contact agency/jurisdiction directly to confirm a particular project.',
  3: 'This agency has a moratorium in place but did not report plans. Please contact agency/jurisdiction directly to confirm potential conflicts.',
  4: 'This agency does not enforce a paving moratorium. Please contact the jurisdiction directly to confirm potential conflicts.',
  5: 'The agency does not have an official moratorium policy in place, but may restrict cuts into certain streets and would evaluate permitting on a case-by-case basis. Please contact the jurisdiction directly to confirm potential conflicts.',
};

type TableData = {
  layerId: string;
  columnProperties: string[];
};

export interface TableProps {
  columns: string[];
  rows: TableData[];
  sortBy?: string;
  reverseSort?: boolean;
  moreInfo?: {
    columns: string[];
    columnProperties: string[];
  };
  label: string;
  emptyMessage?: string;
  size?: 'base' | 'small';
  filter?: { property: string; value: string | number | boolean }[];

  // since we have to do some magical sorting for nearby projects
  // I added this flag to count the features correctly.
  agencyId?: string;
  defaultOpen?: boolean;
}

const cellClasses = 'break-words py-1 text-center';

export default function Table({
  columns,
  rows: data,
  moreInfo,
  sortBy,
  reverseSort,
  label,
  emptyMessage = 'No data found in the project area.',
  size = 'base',
  filter,
  agencyId,
  defaultOpen = false,
}: TableProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { projectIntelligence, config, selectedFeature, citygrid } = useSnapshot(store) as typeof store;
  const [rowCount, setRowCount] = useState<string | number>(0);

  useShouldOpen({ layerIds: data.map((r) => r.layerId), setIsOpen });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const table = useMemo(() => {
    const els: JSX.Element[] = [];

    data.forEach((layerConfig) => {
      const _features = config[layerConfig.layerId];

      if (_features) {
        let features = Object.keys(_features);

        if (filter && filter.length > 0) {
          filter.forEach(({ property, value }) => {
            features = features.filter((featureId) => {
              const f = _features[featureId];
              if (f?.properties && f?.properties[property] === value) {
                return true;
              }

              return false;
            });
          });
        }

        if (sortBy) {
          features = features.sort((a, b) => {
            const pA = _features[a]?.properties;
            const pB = _features[b]?.properties;

            if (pA && pB) {
              const fA = pA[sortBy]?.toString()?.trim();
              const fB = pB[sortBy]?.toString()?.trim();

              if (reverseSort === true && sortBy) {
                return fB?.localeCompare(fA);
              } else {
                return fA?.localeCompare(fB);
              }
            }

            return 0;
          });
        }

        features.forEach((featureId) => {
          const intel = projectIntelligence[featureId];
          const f = config[layerConfig.layerId][featureId];

          if (f && intel) {
            const tr = (
              <tr
                key={`table-row-${featureId}`}
                className="relative cursor-pointer rounded hover:bg-grey-10"
                onMouseEnter={() => store.setHoveredFeature(f)}
                onMouseLeave={() => store.resetHoveredFeature()}
                data-testid={`intelligence-feature-row-${featureId}`}
                onClick={() => {
                  store.setSelectedFeature(f);
                  store.zoomToFeature(f);

                  capturePosthogEvent(POSTHOG_EVENTS.intelligenceFeatureSelected, {
                    project_id: store.project?.id,
                    organization_id: store.project?.organization,
                    accessed_from: 'tabDetails',
                    feature_id: featureId,
                  });
                }}
              >
                <td className={`${cellClasses} text-left`}>
                  <StatusSelect
                    data-testid={`intelligence-status-select-${featureId}`}
                    value={intel.reviewStatus}
                    onChange={(v: string) => {
                      store.updateFeatureStatus(v as IntelligenceStatus, featureId, { layer_id: intel.layerId });
                    }}
                    isActive={selectedFeature?.id?.toString() === featureId}
                    color={getColorFromRgb(intel.rgb) || 'red'}
                  />
                </td>

                <td className={cellClasses}>
                  <div className="flex justify-center">
                    <Comment featureId={featureId} />
                  </div>
                </td>

                {layerConfig.columnProperties.map((property, i) => {
                  let prop: React.ReactNode = '-';

                  if (f.properties?.[property]) {
                    prop = f.properties[property]?.toString()?.trim();
                  }

                  if (property === 'overlap' && f.properties) {
                    if (f.properties[property] === 0) {
                      prop = '< 1%';
                    } else {
                      prop = `${f.properties[property]}%`;
                    }
                  } else if (property === 'projectOverlap' && f.properties) {
                    // Handle both 0 (number) and "0" (string) — the earlier
                    // `if (f.properties?.[property])` guard treats 0 as falsy
                    // and leaves prop as '-', which is indistinguishable from
                    // a missing value. PLA-4108.
                    const val = f.properties[property];
                    if (val != null && val !== '') {
                      prop = String(val);
                    }
                  }

                  return (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                      key={`table-data-${i}`}
                      className={cellClasses}
                    >
                      {prop || '-'}
                    </td>
                  );
                })}

                {moreInfo && (
                  <td
                    key={`table-data-more-info-${featureId}`}
                    className="py-1 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex w-full items-center justify-center">
                      <MoreInfoPopover
                        featureId={featureId}
                        properties={f.properties}
                        agencyId={agencyId}
                        columns={moreInfo.columns}
                        columnProperties={moreInfo.columnProperties}
                      />
                    </div>
                  </td>
                )}
              </tr>
            );

            els.push(tr);
          }
        });
      }
    });

    setRowCount(els.length);

    if (isEmpty(els)) {
      if (agencyId && citygrid[agencyId]) {
        const agency = citygrid[agencyId];

        if (agency?.properties?.published === 0) {
          const status = agency.properties?.data_capture_status;

          if (status && AgencyStatusMap[status]) {
            const text = AgencyStatusMap[status];

            if (text) {
              setRowCount(PENDING);
              return <div className="px-2 text-xs">{text}</div>;
            }
          }
        }
      }

      return <div className="px-2 text-xs">{emptyMessage}</div>;
    }

    const NARROW_COLUMN_WIDTHS: Record<string, number> = {
      'Year Start': 10,
      'Year End': 10,
      More: 8,
    };
    const narrowTotal = columns.reduce((sum, c) => sum + (NARROW_COLUMN_WIDTHS[c] ?? 0), 0);
    const regularCount = columns.filter((c) => NARROW_COLUMN_WIDTHS[c] == null).length;
    // Comments + each regular data column share the leftover space evenly so the
    // Comments icon doesn't look "pushed left" relative to wider data cells.
    const sharedWidthPct = (100 - 30 - narrowTotal) / (regularCount + 1);

    return (
      <table className={`relative w-full table-fixed text-xs`}>
        <thead className="m-2">
          <tr className="m-2 border-b">
            <th className={`p-1 ${cellClasses}`} style={{ width: '30%' }}>
              Status
            </th>
            <th className={cellClasses} style={{ width: `${sharedWidthPct}%` }}>
              Comments
            </th>

            {columns.map((c, i) => {
              const widthPct = NARROW_COLUMN_WIDTHS[c] ?? sharedWidthPct;
              return (
                <th
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  key={`table-heading-${i}`}
                  className={cellClasses}
                  style={{ width: `${widthPct}%` }}
                >
                  {c}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{els}</tbody>
      </table>
    );
  }, [columns, data, projectIntelligence, selectedFeature, citygrid]);

  const count = useMemo(() => {
    let text: string | number = rowCount;

    if (rowCount === 0) {
      text = NO_CONFLICTS;
    }

    return <span className="text-sm">{`(${text})`}</span>;
  }, [rowCount]);

  return (
    <div className={`flex flex-col rounded ${size === 'small' ? 'border-none' : 'border'}`}>
      <div
        data-testid={`intelligence-subsection-toggle-${label}`}
        onClick={() => {
          const open = !isOpen;
          setIsOpen(open);

          // we really only care if users OPEN the section.
          // It shouldn't matter whether they close it
          if (store.project && open === true) {
            capturePosthogEvent(POSTHOG_EVENTS.intelligenceSubsectionExpanded, {
              project_id: store.project.id,
              organization_id: store.project.organization,
              subsection_name: label,
              feature_count: rowCount,
              ...(agencyId && { agency_id: agencyId }),
              ...(agencyId &&
                citygrid[agencyId]?.properties?.agencyname && {
                  agency_name: citygrid[agencyId].properties.agencyname,
                }),
            });
          }
        }}
        className={`flex w-[calc(100%-0.5rem)] cursor-pointer items-center justify-between rounded p-1 ${
          size === 'small' ? 'mx-2 mt-2 text-[15px]' : 'py-1 px-2'
        } hover:bg-grey-20`}
      >
        <div className="flex items-center gap-2 font-medium">
          {label} {count}
        </div>
        <div>
          <Icon icon={isOpen ? 'chevronUp' : 'chevronDown'} />
        </div>
      </div>

      {isOpen && (
        <div className="p-1">
          {agencyId && citygrid[agencyId] && (
            <AgencyDataStatus
              researchStatus={citygrid[agencyId]?.properties?.research_status}
              lastRefresh={citygrid[agencyId]?.properties?.last_refresh}
              testIdSuffix={citygrid[agencyId]?.properties?.agencyname}
            />
          )}
          {table}
        </div>
      )}
    </div>
  );
}

// Trigger + popover for the per-row detail card. The trigger's `selected`
// (blue) state applies only while the popover is open; at rest it is a
// standard secondary outline button.
function MoreInfoPopover(props: React.ComponentProps<typeof FeatureDetailCard>) {
  return (
    <Popover
      side="bottom"
      align="end"
      sideOffset={10}
      trigger={(open) => (
        <UiButton
          variant={open ? 'selected' : 'secondary'}
          size="icon"
          aria-label="More info"
          data-testid={`intelligence-more-info-trigger-${props.featureId}`}
        >
          <UiIcon icon="questionMarkCircled" size={16} />
        </UiButton>
      )}
    >
      {() => <FeatureDetailCard {...props} />}
    </Popover>
  );
}
